import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, FileClock } from "lucide-react";

const ACTION_COLORS = {
  redeem_code: "text-amber-300",
  trade_create: "text-cyan-300",
  trade_accepted: "text-emerald-300",
  trade_rejected: "text-red-400",
  trade_cancelled: "text-white/50",
  request_approved: "text-emerald-300",
  request_denied: "text-red-400",
  request_mint_on_approve: "text-violet-300",
  grant_credits: "text-amber-300",
  bulk_create_templates: "text-cyan-300",
};

export default function AdminAuditLog() {
  const [action, setAction] = useState("all");
  const [search, setSearch] = useState("");
  const { data: logs } = useQuery({
    queryKey: ["audit-log", action],
    queryFn: async () => {
      const p = new URLSearchParams({ limit: "200" });
      if (action !== "all") p.set("action", action);
      return (await api.get(`/admin/audit-log?${p}`)).data;
    },
  });

  const filtered = (logs || []).filter((l) => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (
      l.action.toLowerCase().includes(s) ||
      (l.actor_name || "").toLowerCase().includes(s) ||
      (l.target_id || "").toLowerCase().includes(s)
    );
  });

  const actions = ["all", "redeem_code", "trade_create", "trade_accepted", "trade_rejected", "trade_cancelled", "request_approved", "request_denied", "grant_credits", "bulk_create_templates"];

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <h1 className="font-display text-3xl text-white tracking-[0.06em]">AUDIT LOG</h1>
        <div className="flex flex-wrap items-center gap-2 w-full lg:w-auto">
          <div className="relative flex-1 min-w-[180px] lg:flex-initial lg:w-64">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
            <Input data-testid="audit-search" placeholder="Search action/actor/target…" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 bg-[hsl(220_18%_12%)] border-white/10 w-full" />
          </div>
          <Select value={action} onValueChange={setAction}>
            <SelectTrigger className="bg-[hsl(220_18%_12%)] border-white/10 w-full sm:w-56"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[hsl(220_18%_10%)] border-white/10">
              {actions.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="hud-pill rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[760px]">
            <thead className="bg-white/5 text-left font-mono text-[10px] uppercase tracking-[0.25em] text-white/50">
              <tr><th className="px-4 py-3">Time</th><th className="px-4 py-3">Actor</th><th className="px-4 py-3">Action</th><th className="px-4 py-3">Target</th><th className="px-4 py-3">Meta</th></tr>
            </thead>
            <tbody>
              {filtered.map((l) => (
                <tr key={l.id} className="border-t border-white/5">
                  <td className="px-4 py-3 font-mono text-xs text-white/60 whitespace-nowrap">{new Date(l.created_at).toLocaleString()}</td>
                  <td className="px-4 py-3 text-white">{l.actor_name || l.actor_id?.slice(0, 8)}</td>
                  <td className={`px-4 py-3 font-mono text-xs uppercase tracking-[0.2em] ${ACTION_COLORS[l.action] || "text-white/70"}`}>{l.action}</td>
                  <td className="px-4 py-3 font-mono text-xs text-white/50">{l.target_type} {l.target_id ? l.target_id.slice(0, 8) : ""}</td>
                  <td className="px-4 py-3 font-mono text-[10px] text-white/40 max-w-[300px] truncate">{JSON.stringify(l.meta || {})}</td>
                </tr>
              ))}
              {filtered.length === 0 && <tr><td colSpan={5} className="px-4 py-12 text-center text-white/40"><FileClock className="h-6 w-6 mx-auto mb-2 opacity-40" /> No audit entries.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
